package com.dynamicruntime.webapp

import com.dynamicruntime.common.context.CL
import com.dynamicruntime.common.content.FragmentAudience
import com.dynamicruntime.common.gedra.sandboxOf
import com.dynamicruntime.common.gedra.sandboxParentOf
import com.dynamicruntime.common.endpoint.EI
import com.dynamicruntime.common.endpoint.EP
import com.dynamicruntime.common.exception.EXC
import com.dynamicruntime.common.fileref.FREF
import com.dynamicruntime.common.gedra.ACEP
import com.dynamicruntime.common.gedra.PlanUsage
import com.dynamicruntime.common.gedra.CCT
import com.dynamicruntime.common.gedra.CFEP
import com.dynamicruntime.common.gedra.CLD
import com.dynamicruntime.common.gedra.COV
import com.dynamicruntime.common.gedra.CIT
import com.dynamicruntime.common.gedra.CPY
import com.dynamicruntime.common.gedra.EDM
import com.dynamicruntime.common.gedra.FSR
import com.dynamicruntime.common.gedra.MNU
import com.dynamicruntime.common.gedra.ClientFieldEdit
import com.dynamicruntime.common.gedra.ClientFieldPolicies
import com.dynamicruntime.common.gedra.GCI
import com.dynamicruntime.common.gedra.UF
import com.dynamicruntime.common.gedra.ClientPresentationFields
import com.dynamicruntime.common.gedra.ClientStatus
import com.dynamicruntime.common.gedra.GedraConfigOrigin
import com.dynamicruntime.common.gedra.IMP
import com.dynamicruntime.common.gedra.ImpactKind
import com.dynamicruntime.common.cfact.CFACT
import com.dynamicruntime.common.home.HFLD
import com.dynamicruntime.common.home.HMENU
import com.dynamicruntime.common.uiblock.UIB
import com.dynamicruntime.common.user.ADEP
import com.dynamicruntime.common.user.UADEP
import com.dynamicruntime.common.user.normalizeUserLabels
import com.dynamicruntime.common.user.USF
import com.dynamicruntime.common.util.humanizeFieldName
import com.dynamicruntime.common.util.toJsonListOfMaps
import com.dynamicruntime.common.util.toJsonListOfStrings
import com.dynamicruntime.common.util.toJsonMapOrEmpty
import com.dynamicruntime.common.util.toOptStr
import com.dynamicruntime.common.webasset.WAS

/**
 * One client as the Clients page lists it (issue #905): a row of `/clientAdmin/clients/overview`. [status] and
 * [origin] are the wire names (`ClientStatus`, `GedraConfigOrigin`), kept as strings so a value this build does not
 * know still lists rather than dropping the row. [issues] are the messages of what the checks forgave -- or, for a
 * dropped client, why -- so the listing can say it rather than only count it.
 */
class ClientOverview(
    val clientId: String,
    val name: String,
    val status: String,
    val origin: String,
    val storedConfigs: Int,
    val forms: Int,
    val users: Int,
    val unclaimedUsers: Int,
    val workflowCount: Int,
    val hasSurvey: Boolean,
    val issues: List<String>,
    /** How much of its copy and interface the client's own configuration changes (issue #917). */
    val copyOverrides: Int,
    val blockOverrides: Int,
    /** When the client is a sandbox (issue #932): the client whose sandbox it is. */
    val sandboxOf: String? = null,
    /** Beside [sandboxOf], that client's display name (issue #1438); see [sandboxParentName]. */
    val sandboxOfName: String? = null,
    /** Whether the client has a live sandbox (issue #932). */
    val hasSandbox: Boolean = false,
    /** Whether the client runs only its published configuration (issue #1001): every client with a sandbox does. */
    val publishedOnly: Boolean = false,
    /** Whether the client takes nothing stored on this node -- static in production (issue #1001). */
    val staticHere: Boolean = false,
    /** The client's plan and its usage against it (issue #1109); null for a client on none. */
    val planUsage: PlanUsage? = null,
    /** The customer whose root the client is (issue #1182), which may not be retired; null for any other client. */
    val rootOf: String? = null,
    /** The most forms a recalculation of its form states from its page may take (issue #1412). */
    val formStatesRecalcLimit: Int = 0,
    /**
     * How many forms that recalculation would look at (issue #1412): what the limit is held against, counted as the
     * backend counts it -- not necessarily [forms].
     */
    val formStatesRecalcCount: Int = 0,
)

/** The overview endpoint's items as rows; one without a client id is not a client. Pure, and covered under `jsNodeTest`. */
fun parseClientOverview(items: List<Map<String, Any?>>): List<ClientOverview> = items.mapNotNull { row ->
    fun int(key: String): Int = (row[key] as? Number)?.toInt() ?: 0
    val clientId = row[CLD.clientId].toOptStr() ?: return@mapNotNull null
    ClientOverview(
        clientId = clientId,
        name = row[CLD.name].toOptStr().orEmpty(),
        status = row[CLD.status].toOptStr().orEmpty(),
        origin = row[CLD.origin].toOptStr().orEmpty(),
        storedConfigs = int(CLD.storedConfigs),
        forms = int(CLD.forms),
        users = int(CLD.users),
        unclaimedUsers = int(CLD.unclaimedUsers),
        workflowCount = int(CLD.workflowCount),
        hasSurvey = row[CLD.hasSurvey] == true,
        issues = row[CLD.issues].toJsonListOfMaps().mapNotNull { it[GCI.message].toOptStr() },
        copyOverrides = int(CLD.copyOverrides),
        blockOverrides = int(CLD.blockOverrides),
        sandboxOf = row[CLD.sandboxOf].toOptStr(),
        sandboxOfName = row[CLD.sandboxOfName].toOptStr(),
        hasSandbox = row[CLD.hasSandbox] == true,
        publishedOnly = row[CLD.publishedOnly] == true,
        staticHere = row[CLD.staticHere] == true,
        planUsage = PlanUsage.fromInfo(row[CLD.planUsage].toJsonMapOrEmpty().ifEmpty { null }),
        rootOf = row[CLD.rootOf].toOptStr(),
        formStatesRecalcLimit = int(FSR.limit),
        formStatesRecalcCount = int(FSR.count),
    )
}

/**
 * A client's plan as its summary says it (issue #1109): the plan's name, then how much of each measure it limits the
 * client holds -- `Free: 12 of 100 forms, 3 of 20 users, 1 of 4 workflows, 2 of 10 schemas`. A plan that limits
 * nothing reads as its name alone. Pure, and covered under `jsNodeTest`.
 */
fun planUsageText(usage: PlanUsage): String {
    val name = usage.plan.replaceFirstChar { it.uppercase() }
    if (usage.measures.isEmpty()) return name
    return "$name: " + usage.measures.joinToString(", ") { "${it.used} of ${it.limit} ${it.measure}" }
}

/**
 * The listing's rows with each sandbox right after its parent (issue #932), so the two read as a pair; a sandbox
 * whose parent is not listed keeps its place. Pure, and covered under `jsNodeTest`.
 */
fun withSandboxesBesideParents(rows: List<ClientOverview>): List<ClientOverview> {
    val listed = rows.map { it.clientId }.toSet()
    val byParent = rows.filter { it.sandboxOf != null && it.sandboxOf in listed }.groupBy { it.sandboxOf }
    return rows.filter { it.sandboxOf == null || it.sandboxOf !in listed }.flatMap { listOf(it) + byParent[it.clientId].orEmpty() }
}

/**
 * A sandbox's client as the page names it in words (issue #1438): by its name, as its people know it -- `Acme`, not
 * `acme` -- or by its id when the row carries no name. Null for a client that is not a sandbox.
 */
val ClientOverview.sandboxParentName: String?
    get() = sandboxOf?.let { parent -> sandboxOfName?.takeIf { it.isNotBlank() } ?: parent }

/** What the listing's client cell adds for a sandbox: "sandbox of Acme"; null for any other client. */
fun sandboxRowNote(row: ClientOverview): String? = row.sandboxParentName?.let { "sandbox of $it" }

/**
 * What the client detail's Sandbox section says, and the one action it offers (issue #932): [add] true to add a
 * sandbox, false to remove it, null for none.
 */
class SandboxControl(val text: String, val add: Boolean?)

/**
 * Where a sandbox's page leads back to its client (issue #1422): the client's id, when this is a sandbox's page and the
 * caller can go there -- by switching back to their own user in the client ([wayBackUserId], in a sandbox session), or,
 * for an administrator who sees every client and is not in one, by following the link. Null otherwise: not a sandbox's
 * page, or a sandbox session with no user to go back to. Not conditional on anything being published. Pure.
 */
fun sandboxWayBackTarget(row: ClientOverview?, acrossClients: Boolean, inSandbox: Boolean, wayBackUserId: Long?): String? {
    val parent = row?.sandboxOf ?: return null
    return parent.takeIf { if (inSandbox) wayBackUserId != null else acrossClients }
}

/**
 * The detail's Sandbox section for [row] (issue #932). A sandbox says whose it is; a client defined in source code
 * says its flag is set there; a stored, present client offers adding or removing one, saying what follows. Pure,
 * and covered under `jsNodeTest`.
 */
fun sandboxControl(row: ClientOverview): SandboxControl {
    row.sandboxParentName?.let {
        return SandboxControl("This is the sandbox of $it: it runs $it's latest configuration, published or not, with users and data of its own.", null)
    }
    if (row.origin == GedraConfigOrigin.source.name) {
        return SandboxControl(
            if (row.hasSandbox) "Has a sandbox, set in its source code." else "Defined in source code; a sandbox is turned on there.",
            null,
        )
    }
    if (row.status != ClientStatus.present.name) return SandboxControl("Not loaded on this node, so its sandbox cannot be changed here.", null)
    return if (row.hasSandbox) {
        SandboxControl(
            "Has a sandbox, which runs this client's unpublished configuration; the client itself runs only what is " +
                "published. Removing it keeps the sandbox's users and data for if it is added again.",
            false,
        )
    } else {
        SandboxControl(
            "No sandbox. Adding one gives a place to preview unpublished configuration, and makes the client run only " +
                "what is published.",
            true,
        )
    }
}

private fun issues(n: Int): String = if (n == 1) "1 issue" else "$n issues"

/**
 * What the detail's Retirement section says, and the one action it offers (issue #1182): [retire] true to retire the
 * client, false to bring it back, null for none.
 */
class RetirementControl(val text: String, val retire: Boolean?)

/**
 * The detail's Retirement section for [row], for an administrator who sees every client (issue #1182). The action is
 * offered only where the backend's switch (`/admin/client/retirement/set`, #1111) can take it, and each place it
 * cannot says why in the backend's terms: a sandbox goes with its parent, a client defined in source code is retired
 * there, a customer's root waits for another root, and a definition whose configuration [storedConfig] has unpublished
 * changes ([configs] say so) would publish them. Pure, and covered under `jsNodeTest`.
 */
fun retirementControl(row: ClientOverview, storedConfig: String?, configs: List<ConfigSummaryView>?): RetirementControl {
    row.sandboxParentName?.let { return RetirementControl("A sandbox is retired with its parent, $it.", null) }
    if (row.origin == GedraConfigOrigin.source.name) {
        return RetirementControl("Defined in source code, so it is retired there.", null)
    }
    if (row.status == ClientStatus.retired.name) {
        return RetirementControl(
            "Retired: everything it stored is kept, and nobody can sign in to it. Bringing it back restores it as it was.",
            false,
        )
    }
    row.rootOf?.let {
        return RetirementControl("The root of the customer '$it', so it is retired only once the customer names another root.", null)
    }
    if (row.status != ClientStatus.present.name) return RetirementControl("Not loaded on this node, so it cannot be retired here.", null)
    val holder = storedConfig?.let { name -> configs?.firstOrNull { it.name == name } }
    if (holder != null && !holder.published) {
        return RetirementControl(
            "Its definition is in configuration '$storedConfig', which has unpublished changes that retiring it would " +
                "publish with it. Publish or revert it first.",
            null,
        )
    }
    return RetirementControl("In use. Retiring it keeps everything it stored, and it can be brought back as it was.", true)
}

/** What a retirement does, said in its confirmation (issue #1182). */
const val retireConsequence =
    "Its data is kept, its users can no longer sign in, their open sessions end, and its sandbox goes with it. " +
        "It can be brought back as it was."

/**
 * How a client's load reads (issue #905): loaded, with any issues the checks forgave; or why it is not carried here.
 * An unknown status is shown as its name rather than hidden. Pure, and covered under `jsNodeTest`.
 */
fun clientLoadText(status: String, issueCount: Int): String = when (status) {
    ClientStatus.present.name -> if (issueCount == 0) "Loaded" else "Loaded, ${issues(issueCount)} forgiven"
    ClientStatus.notEnabled.name -> "Not enabled here"
    ClientStatus.retired.name -> "Retired"
    ClientStatus.dropped.name -> "Dropped by a check" + (if (issueCount == 0) "" else " \u2014 ${issues(issueCount)}")
    ClientStatus.storedOnly.name -> "Stored config only"
    else -> status
}

/**
 * Where a client's definition comes from, as the listing says it (issue #905): "Source", or "Source + 2 stored" when
 * stored overlays are loaded on it; "Stored", or "Stored (2 configs)" -- a stored definition counts its own config,
 * so it is never "+ N". Pure, and covered under `jsNodeTest`.
 */
fun clientOriginText(origin: String, storedConfigs: Int): String = when (origin) {
    GedraConfigOrigin.source.name -> if (storedConfigs == 0) "Source" else "Source + $storedConfigs stored"
    GedraConfigOrigin.stored.name -> if (storedConfigs <= 1) "Stored" else "Stored ($storedConfigs configs)"
    else -> origin
}

/** A client's active users with the unclaimed ones told apart: "12", or "12 (3 unclaimed)". Pure, and covered under `jsNodeTest`. */
fun userCountText(users: Int, unclaimed: Int): String = if (unclaimed == 0) "$users" else "$users ($unclaimed unclaimed)"

/** A client's workflows, noting a survey: "4", "4, survey". Pure, and covered under `jsNodeTest`. */
fun workflowsText(count: Int, hasSurvey: Boolean): String = if (hasSurvey) "$count, survey" else "$count"

/**
 * Where a client's Forms count leads (issue #905): My forms with that client chosen, for an administrator who sees
 * across clients -- the listing's `client` is its chosen-client selector (#714). A client-scoped administrator's
 * listing is their own client already, so their link carries nothing: the page would drop the key anyway
 * (`formsInitialSearch`). Pure, and covered under `jsNodeTest`.
 */
fun clientFormsHref(clientId: String, acrossClients: Boolean): String =
    routeHref(listOf(HP.page to HMENU.pageForms) + if (acrossClients) listOf(EI.client to clientId) else emptyList())

/**
 * Where a client's Users count leads (issue #905): the Users page filtered to that client, for an administrator who
 * sees across clients -- `client` is an allClients-only filter there, and the page draws no control for it to
 * anyone else, so a scoped administrator's link carries nothing rather than a filter they could not clear. Pure,
 * and covered under `jsNodeTest`.
 */
fun clientUsersHref(clientId: String, acrossClients: Boolean): String =
    routeHref(listOf(HP.page to HMENU.pageUsers) + if (acrossClients) listOf(USF.client to clientId) else emptyList())

/**
 * One client's own page (issue #906): its definition, issues, and stored configuration. Any administrator who may
 * see the client may open it -- a client-scoped one their own, which is the only one a link of theirs can name.
 * Pure, and covered under `jsNodeTest`.
 */
fun clientDetailHref(clientId: String): String = routeHref(listOf(HP.page to HMENU.pageClients, HP.client to clientId))

/**
 * Which client the Clients page shows (issue #1082), or null for the listing.
 *
 * A client the address names ([routeClient], `c=<id>`) is the one, as it always was. With none named, an administrator
 * of **one** client -- one who may administer and does not see across clients -- is shown that client, [ownClient],
 * in place: their listing was a table of one row to click through on every visit, and the page they came for is
 * the one behind it. An administrator who sees across clients gets the listing.
 *
 * Not for an administrator in `public` without `allClients`, who administers only their own users (issue #805):
 * the overview endpoint refuses them, and the page says so in the endpoint's words rather than opening a detail
 * that would refuse them piece by piece. And not before the shell has said who is asking -- [canManageUsers] and
 * [canSeeAllClients] are false until then -- so nothing is fetched for a caller on a guess. Pure, and covered under
 * `jsNodeTest`.
 */
fun clientsOpenId(routeClient: String?, canManageUsers: Boolean, canSeeAllClients: Boolean, ownClient: String): String? = when {
    routeClient != null -> routeClient
    canManageUsers && !canSeeAllClients && ownClient.isNotEmpty() && ownClient != CL.public -> ownClient
    else -> null
}

/**
 * Whether the open client [openId] is the one the Clients page shows with **none named** (issue #1082): the
 * caller's one client, open in place. Then the page has no listing behind it, and both of its addresses -- with the
 * client named and without -- are the same page. Pure, and covered under `jsNodeTest`.
 */
fun clientOpenInPlace(openId: String?, canManageUsers: Boolean, canSeeAllClients: Boolean, ownClient: String): Boolean =
    openId != null && openId == clientsOpenId(null, canManageUsers, canSeeAllClients, ownClient)

/**
 * Whether a client's detail offers its way back (issue #1082). Not when it is open [inPlace] and the way back
 * would be the Clients listing, which for that caller only opens this page again. A listing the page was opened
 * *from* ([from], the address's `from=`) is still somewhere to go back to. Pure, and covered under `jsNodeTest`.
 */
fun clientBackOffered(inPlace: Boolean, from: String?): Boolean =
    !inPlace || backTarget(from, HMENU.pageClients) != HMENU.pageClients

/**
 * Whether [params] is still [clientId]'s own page (issue #1082), for a guard that asks before unsaved work is left:
 * the Clients page naming that client -- or, when the client is the one the page opens [inPlace], naming none. So
 * an administrator of one client is not asked whether to leave a page they are staying on, by a move between its
 * two addresses. Pure, and covered under `jsNodeTest`.
 */
fun staysOnClientPage(params: Map<String, String>, clientId: String, inPlace: Boolean): Boolean =
    params[HP.page] == HMENU.pageClients && (params[HP.client] == clientId || inPlace && params[HP.client] == null)

/**
 * Where a user's Forms link leads (issue #1081): the forms listing confined to that one user.
 *
 * **By id, never by address.** The listing's `user` takes either, but an address names a *person*: the backend
 * resolves it to their default user, which for somebody with users in two clients -- or two personas in one -- is
 * not necessarily the row the link was on. An id is that row.
 *
 * [surfaceClient] is the client the listing is to be drawn for ([userFormsSurface]), or null to choose none: the
 * page keeps both keys when it loads (`formsInitialSearch`). Pure, and covered under `jsNodeTest`.
 */
fun userFormsHref(userId: Long, surfaceClient: String?): String = routeHref(
    listOf(HP.page to HMENU.pageForms, EI.user to userId.toString()) +
        if (surfaceClient != null) listOf(EI.client to surfaceClient) else emptyList(),
)

/**
 * The client a user's Forms link chooses on the forms listing (issue #1081), or null for none: the user's own
 * [client] when it is among [carried], the clients this node carries as the page was told them. Choosing it draws
 * the listing on that client's surface -- its columns and filters -- rather than the administrator's own.
 *
 * Null in the two cases where naming the client could only go wrong. A client-scoped administrator is told of no
 * clients ([carried] is empty): their listing is their client already, and the page would drop the key, as
 * [clientFormsHref] leaves it out. And a user whose client this node does **not** carry -- one not enabled in this
 * environment, or dropped by a check -- has no surface to draw: the listing would fail to load, where without the
 * key it lists that user's forms on the administrator's own surface, which is still every form they own. Pure, and
 * covered under `jsNodeTest`.
 */
fun userFormsSurface(client: String, carried: Set<String>): String? = client.takeIf { it in carried }

/**
 * How much a client customizes, as the listing's Customized column says it (issue #917): "3 copy, 2 menu", either
 * half alone, or a dash for none. "Menu" for the interface changes because every block a client can overlay today
 * is one. Pure, and covered under `jsNodeTest`.
 */
fun customizedText(copy: Int, blocks: Int): String = listOfNotNull(
    "$copy copy".takeIf { copy > 0 },
    "$blocks menu".takeIf { blocks > 0 },
).joinToString(", ").ifEmpty { "\u2014" }

/** One piece of copy a client's own configuration sets (issue #917): a row of `/clientAdmin/client/overrides`' `copy`. */
class CopyOverrideView(
    val fileId: String,
    val namespace: String,
    val key: String,
    val audience: String,
    /** What everybody else reads; null when nothing else sets the key. */
    val baseValue: String?,
    val value: String?,
    val configName: String?,
    val origin: String,
    /** The client's source value a stored config overrides, when one does. */
    val sourceValue: String?,
    val orphan: Boolean,
    /** Where the application shows the file (issue #933); null for one it does not show. */
    val shownOn: String?,
    /** The template whose configuration set the value, when the client's own did not (issue #945). */
    val template: String? = null,
)

/** One field of an interface item a client sets (issue #917). Values are text as the endpoint rendered them. */
class BlockFieldView(
    val field: String,
    val baseValue: String?,
    val value: String?,
    val configName: String?,
    val origin: String,
    /** The template whose configuration set the field, when the client's own did not (issue #945). */
    val template: String? = null,
)

/** One interface item or object a client's own configuration changes (issue #917): a row of the endpoint's `blocks`. */
class BlockOverrideView(
    val blockId: String,
    val path: String,
    /** The item's key within its list; null for an object outside one, or an item added with no key. */
    val itemId: String?,
    val added: Boolean,
    val hidden: Boolean,
    /** The item's label in the block everybody else gets, when it has one. */
    val baseLabel: String?,
    val fields: List<BlockFieldView>,
)

/** What one client's own configuration changes (issue #917): the endpoint's item, parsed. */
class ClientOverridesView(val clientId: String, val copy: List<CopyOverrideView>, val blocks: List<BlockOverrideView>)

/**
 * The overrides item as a [ClientOverridesView]. A copy row without its three-part address, or a block row without a
 * block, is not an override. Pure, and covered under `jsNodeTest`.
 */
fun parseClientOverrides(item: Map<String, Any?>): ClientOverridesView = ClientOverridesView(
    clientId = item[COV.client].toOptStr().orEmpty(),
    copy = item[COV.copy].toJsonListOfMaps().mapNotNull { row ->
        CopyOverrideView(
            fileId = row[COV.fileId].toOptStr() ?: return@mapNotNull null,
            namespace = row[COV.namespaceField].toOptStr() ?: return@mapNotNull null,
            key = row[COV.key].toOptStr() ?: return@mapNotNull null,
            audience = row[COV.audience].toOptStr().orEmpty(),
            baseValue = row[COV.baseValue].toOptStr(),
            value = row[COV.value].toOptStr(),
            configName = row[COV.configName].toOptStr(),
            origin = row[COV.origin].toOptStr().orEmpty(),
            sourceValue = row[COV.sourceValue].toOptStr(),
            orphan = row[COV.orphan] == true,
            shownOn = row[COV.shownOn].toOptStr(),
            template = row[COV.template].toOptStr(),
        )
    },
    blocks = item[COV.blocks].toJsonListOfMaps().mapNotNull { row ->
        BlockOverrideView(
            blockId = row[COV.blockId].toOptStr() ?: return@mapNotNull null,
            path = row[COV.path].toOptStr().orEmpty(),
            itemId = row[COV.itemId].toOptStr(),
            added = row[COV.added] == true,
            hidden = row[COV.hidden] == true,
            baseLabel = row[COV.baseLabel].toOptStr(),
            fields = row[COV.fields].toJsonListOfMaps().mapNotNull { f ->
                BlockFieldView(
                    field = f[COV.field].toOptStr() ?: return@mapNotNull null,
                    baseValue = f[COV.baseValue].toOptStr(),
                    value = f[COV.value].toOptStr(),
                    configName = f[COV.configName].toOptStr(),
                    origin = f[COV.origin].toOptStr().orEmpty(),
                    template = f[COV.template].toOptStr(),
                )
            },
        )
    },
)

/**
 * What an interface row is within its block: the item's key, "(new item)" for one added with no key, the path of
 * an object outside a list, or "(block)" for the block's own fields. Pure, and covered under `jsNodeTest`.
 */
fun blockItemName(row: BlockOverrideView): String = when {
    row.itemId != null -> row.itemId
    row.added -> "(new item)"
    row.path.isEmpty() -> "(block)"
    else -> row.path
}

/** An interface row's address: the block and [blockItemName] within it. */
fun blockItemText(row: BlockOverrideView): String = "${row.blockId}: ${blockItemName(row)}"

/** The row's own `label` field, when the client set one. */
private fun labelField(row: BlockOverrideView): BlockFieldView? = row.fields.firstOrNull { it.field == HFLD.label }

/**
 * What the client did to an interface item, in a word each (issue #917): added; hidden; shown (a condition that was
 * `#never` and no longer is); "condition changed" for any other condition the client set; renamed (a label);
 * reordered (a display order); and "changed" for anything else -- joined when several apply, so a rename beside a
 * hide reads "hidden, renamed". Pure, and covered under `jsNodeTest`.
 */
fun menuChangeText(row: BlockOverrideView): String {
    val fields = row.fields.associateBy { it.field }
    val condition = fields[UIB.cfactExpression]
    val words = buildList {
        if (row.added) add("added")
        if (row.hidden) add("hidden")
        if (!row.added && !row.hidden && condition != null) {
            add(if (condition.baseValue == CFACT.neverName && condition.value != CFACT.neverName) "shown" else "condition changed")
        }
        if (!row.added && HFLD.label in fields) add("renamed")
        if (!row.added && UIB.displayOrder in fields) add("reordered")
        if (isEmpty() && fields.isNotEmpty()) add("changed")
    }
    return words.joinToString(", ")
}

/**
 * The value an interface row shows for the client: its label when it set one; nothing for a row that only hides or
 * shows the item (the change column says it, and the condition is not a value anybody reads); else the one field's
 * value, else the fields as `name: value`. Pure, and covered under `jsNodeTest`.
 */
fun blockValueText(row: BlockOverrideView): String {
    labelField(row)?.let { return it.value.orEmpty() }
    val words = menuChangeText(row)
    if (words == "hidden" || words == "shown") return ""
    val single = row.fields.singleOrNull()
    if (single != null) return single.value.orEmpty()
    return row.fields.joinToString(", ") { "${it.field}: ${it.value.orEmpty()}" }
}

/**
 * An interface row in a phrase, for a list that has no columns (issue #917): what the client did, and the label when
 * it set one -- "renamed: Acme overview", "added: Site audits", "hidden". Pure, and covered under `jsNodeTest`.
 */
fun blockSummaryText(row: BlockOverrideView): String {
    val words = menuChangeText(row)
    val label = labelField(row)?.value ?: return words
    return "$words: $label"
}

/**
 * Which config set a value, and where that config lives: "acmeClient (source)". A value the client inherited from the
 * template it extends (issue #945) names the template instead of the origin -- "starterCopy (template starter)" --
 * since its config is the template's, not the client's. Pure, and covered under `jsNodeTest`.
 */
fun setByText(configName: String?, origin: String, template: String? = null): String {
    val where = if (template != null) "template $template" else origin
    return when {
        configName == null -> where
        where.isEmpty() -> configName
        else -> "$configName ($where)"
    }
}

/**
 * The configs that set an interface row's fields, each once, a stored one first -- it is applied last, so it is the
 * one whose values win where the two set the same field. A row set by one config reads as that config; one set by
 * a source and a stored config names both. Pure, and covered under `jsNodeTest`.
 */
fun blockSetByText(row: BlockOverrideView): String = row.fields
    .sortedByDescending { it.origin == GedraConfigOrigin.stored.name }
    .map { setByText(it.configName, it.origin, it.template) }.distinct().joinToString(", ")

/** One key as the cross-client view lists it (issue #917): who overrides it, and with what. */
class KeyAcrossClients(val group: String, val key: String, val clients: List<Pair<String, String>>)

/**
 * Every overridden key across [byClient], grouped by file or block then key, each with the clients overriding it
 * and their values, in the order the clients were given (issue #917). Copy keys are grouped by file; interface
 * items by block. Pure, and covered under `jsNodeTest`.
 */
fun overridesAcrossClients(byClient: List<Pair<String, ClientOverridesView>>): List<KeyAcrossClients> {
    val keys = LinkedHashMap<Pair<String, String>, MutableList<Pair<String, String>>>()
    for ((clientId, view) in byClient) {
        for (row in view.copy) keys.getOrPut(row.fileId to "${row.namespace}.${row.key}") { mutableListOf() }.add(clientId to row.value.orEmpty())
        for (row in view.blocks) keys.getOrPut(row.blockId to blockItemName(row)) { mutableListOf() }.add(clientId to blockSummaryText(row))
    }
    return keys.entries.sortedWith(compareBy({ it.key.first }, { it.key.second })).map { KeyAcrossClients(it.key.first, it.key.second, it.value) }
}

/** Where the Customized column leads (issue #917): the client's detail, where the Copy & menu section is. */
fun clientOverridesHref(clientId: String): String = clientDetailHref(clientId)

/** The cross-client view of the overrides (issue #917), for an administrator who sees across clients. */
fun overridesAcrossHref(): String = routeHref(listOf(HP.page to HMENU.pageClients, HP.overrides to "1"))

/** One key an administrator may override for a client (issue #918): a row of `/clientAdmin/client/copy/keys`. */
class CopyKeyView(
    val fileId: String,
    val namespace: String,
    val key: String,
    val audience: String,
    val value: String,
    /** Where the application shows the file (issue #933); null for one it does not show. */
    val shownOn: String?,
)

/** The keys listing's items as [CopyKeyView]s; one without its address is not a key. Pure, and covered under `jsNodeTest`. */
fun parseCopyKeys(items: List<Map<String, Any?>>): List<CopyKeyView> = items.mapNotNull { row ->
    CopyKeyView(
        fileId = row[COV.fileId].toOptStr() ?: return@mapNotNull null,
        namespace = row[COV.namespaceField].toOptStr() ?: return@mapNotNull null,
        key = row[COV.key].toOptStr() ?: return@mapNotNull null,
        audience = row[COV.audience].toOptStr().orEmpty(),
        value = row[COV.value].toOptStr().orEmpty(),
        shownOn = row[COV.shownOn].toOptStr(),
    )
}

/** What the picker says of a file the application does not show (issue #933). */
const val notShownNote = "Not shown by this application"

/** One file as the picker offers it: its id and where it is shown, null for a file the application does not show. */
class CopyFileChoice(val fileId: String, val shownOn: String?)

/**
 * The files the File choice offers, from the keys (issue #933): the ones the application shows first, each
 * with where, then the ones it does not -- offered still, since a deployment's own frontend may read a file by
 * URL, but set apart under [notShownNote]. Pure, and covered under `jsNodeTest`.
 */
fun copyFileChoices(keys: List<CopyKeyView>): List<CopyFileChoice> {
    val byFile = LinkedHashMap<String, String?>()
    for (k in keys) if (k.fileId !in byFile) byFile[k.fileId] = k.shownOn
    val (shown, unshown) = byFile.entries.partition { it.value != null }
    return (shown + unshown).map { CopyFileChoice(it.key, it.value) }
}

/** How the picker labels a file: the id and where it is shown -- "home — the app bar and home page". */
fun copyFileLabel(choice: CopyFileChoice): String = choice.shownOn?.let { "${choice.fileId} \u2014 $it" } ?: choice.fileId

/**
 * What the copy editor says about a value's template syntax, by who the file is for (issue #1001): a frontend file's
 * `${...}` placeholders are filled as the page renders, and a server-side `%{...}` is refused there; a backend file
 * is resolved on the server -- `${...}` from what sends it, `%{@t(...)}` pulling in other copy. Said before a save,
 * so the backend's refusal is not the first a writer hears of it. Pure, and covered under `jsNodeTest`.
 */
fun copySyntaxHint(audience: String): String = when (audience) {
    FragmentAudience.frontend.name ->
        $$"Markdown for the page: ${...} placeholders, such as ${user.publicName}, are filled in as it renders; " +
            "a server-side %{...} is refused here. ![alt](ref:logo) shows the client's file ref logo as an image."
    FragmentAudience.backend.name ->
        $$"Markdown resolved on the server: ${...} is filled from what sends it (a mail's code or address, say), " +
            "and %{@t(\"file.namespace.key\")} pulls in other copy. An image ![alt](ref:logo) shows as its alt " +
            "text: a mail is read where none of the client's files can be fetched."
    else -> "Markdown."
}

/**
 * What a one-key set or reset did (issue #918): where it landed, what the client now reads, and how it took effect --
 * an `EDM` value, live, or a draft its sandbox runs (issue #930). Design View's shared wording (#1010) saves this way.
 */
class CopyEditResult(val configName: String, val value: String?, val stored: Boolean, val issues: List<String>, val mode: String = EDM.live)

/** The set/reset result as a [CopyEditResult]. Pure, and covered under `jsNodeTest`. */
fun parseCopyEditResult(results: Map<String, Any?>): CopyEditResult = CopyEditResult(
    configName = results[COV.configName].toOptStr().orEmpty(),
    value = results[COV.value].toOptStr(),
    stored = results[CPY.stored] == true,
    issues = results[CPY.issues].toJsonListOfMaps().mapNotNull { it[GCI.message].toOptStr() },
    mode = results[CPY.mode].toOptStr() ?: EDM.live,
)

/**
 * Whether a save made on this client's page is a **draft** (issues #1176, #1191): the client has a sandbox
 * ([hasSandbox]), or is one ([sandboxOf] is its parent). The one test every editor on the page words its save by
 * ([saveEffectText]) and says it expects by ([expectedMode]), from the overview row it last read, so the two cannot
 * come to differ. Pure, and covered under `jsNodeTest`.
 */
fun savesAsDraft(sandboxOf: String?, hasSandbox: Boolean): Boolean = sandboxOf != null || hasSandbox

/**
 * How the page expects a save to take effect, which it sends with each one (`mode`). The backend decides at the
 * request, by whether the client has a sandbox *then*; the page said which before the person acted, from what it last
 * read. A save the page called a draft that would be live is refused, and not made ([saveConflict]): a change the
 * person meant to look at first must not reach the client's people because the sandbox went in between. One called
 * live that is a draft goes ahead, and its note says so ([savedNote]).
 */
fun expectedMode(draft: Boolean): String = if (draft) EDM.draft else EDM.live

/** What an editor says beside its save, before the person acts: how the save will take effect. Pure, and covered under `jsNodeTest`. */
fun saveEffectText(draft: Boolean): String =
    if (draft) "Saving keeps it a draft: the client's sandbox shows it, and it goes live once published."
    else "Saving makes it live for the client's people at once."

/**
 * Whether a save made on [clientId]'s page is a draft, as the page last read it: the listing's [row] when it holds
 * one, else what is known without it -- whether the client is a sandbox, and whether its definition ([defSandbox])
 * asks for one. The one answer every editor on the page says beside its save and sends with it (issue #1191). Pure,
 * and covered under `jsNodeTest`.
 */
fun pageSavesDraft(clientId: String, row: ClientOverview?, defSandbox: Boolean): Boolean =
    row?.let { savesAsDraft(it.sandboxOf, it.hasSandbox) } ?: savesAsDraft(sandboxParentOf(clientId), defSandbox)

/**
 * What an editor shows for a save the backend refused because it would not take effect as the page said (issue
 * #1191) -- a 409 carrying [EDM.notAsExpected]: the backend's account of what changed, and that the page has been read
 * again, which the caller does ([saveFailure]). Null for any other failure, a 409 for another reason included. Pure,
 * and covered under `jsNodeTest`.
 */
fun saveConflict(e: Throwable): DisplayError? =
    (e as? ApiError)?.takeIf { it.status == EXC.conflict && it.errorCode == EDM.notAsExpected }?.let {
        DisplayError("${it.message} The page has been read again, and now says how a save takes effect.", DisplayError.Kind.message)
    }

/**
 * What an editor shows for a save that failed with [e]: [saveConflict]'s account, having called [reread] so the page
 * reads the client again, or the failure as any other is shown ([userFacingError]). The one catch every editor that
 * sends [expectedMode] answers its save's failure with.
 */
fun saveFailure(e: Throwable, reread: () -> Unit): DisplayError = saveConflict(e)?.also { reread() } ?: userFacingError(e)

/**
 * The note an editor shows after a save (issue #930): [done]'s account of what changed, and -- for a draft, which a
 * client with a Shadow Sandbox saves -- where it can be seen and how it goes live. Pure, and covered under `jsNodeTest`.
 */
fun savedNote(done: String, mode: String): String =
    if (mode == EDM.draft) "$done Saved as a draft: the client's sandbox shows it, and it goes live once published." else done

/**
 * Whether a copy row offers a reset (issue #918): only a **stored** value is data's to remove. A row set in source
 * shows its value and can be overridden, and once overridden the stored value resets to the source one -- the
 * report's `sourceValue`. Pure, and covered under `jsNodeTest`.
 */
fun copyRowResettable(row: CopyOverrideView): Boolean = row.origin == GedraConfigOrigin.stored.name

/** One home-menu item as a client sees it (issue #919): a row of `/clientAdmin/client/menu/items`. */
class MenuItemView(
    val itemId: String,
    val parentId: String?,
    val baseLabel: String?,
    val label: String?,
    val baseCondition: String?,
    val condition: String?,
    /**
     * Whether the item is the client's own, which the shipped menu does not hold (issue #1094): it has no shipped
     * state to compare with or go back to, and the editor's actions -- all of which change a shipped item -- do not
     * apply to it.
     */
    val added: Boolean = false,
    /**
     * What the audience of [baseCondition] and of [condition] is called (issue #1094), as the menu's own table of
     * audiences names it; null for a withdrawn item and for a condition that table does not hold.
     */
    val baseAudience: String? = null,
    val audience: String? = null,
    /** The audiences this item may be shown to, as the backend lists them -- exactly what a show accepts. */
    val audiences: List<MenuAudienceChoice> = emptyList(),
    /** Whether the client's stored configuration changes the item -- what a reset would remove. */
    val stored: Boolean,
    /**
     * Whether the item shipped for an audience that is the deployment's own (issue #1397) -- its operators, a test
     * instance, a debug session -- which the client's people are never among; the backend's to say.
     */
    val deploymentOnly: Boolean = false,
)

/** An audience a menu item may be shown to (issue #1094): the [condition] a show sends, and the [name] it is chosen by. */
class MenuAudienceChoice(val condition: String, val name: String)

/** The items listing as [MenuItemView]s; one without an id is not an item. Pure, and covered under `jsNodeTest`. */
fun parseMenuItems(items: List<Map<String, Any?>>): List<MenuItemView> = items.mapNotNull { row ->
    MenuItemView(
        itemId = row[COV.itemId].toOptStr() ?: return@mapNotNull null,
        parentId = row[MNU.parentId].toOptStr(),
        baseLabel = row[COV.baseLabel].toOptStr(),
        label = row[MNU.label].toOptStr(),
        baseCondition = row[MNU.baseCondition].toOptStr(),
        condition = row[MNU.condition].toOptStr(),
        added = row[COV.added] == true,
        baseAudience = row[MNU.baseAudience].toOptStr(),
        audience = row[MNU.audience].toOptStr(),
        audiences = row[MNU.audiences].toJsonListOfMaps().mapNotNull { choice ->
            MenuAudienceChoice(choice[MNU.condition].toOptStr() ?: return@mapNotNull null, choice[MNU.name].toOptStr() ?: return@mapNotNull null)
        },
        stored = row[CPY.stored] == true,
        deploymentOnly = row[MNU.deploymentOnly] == true,
    )
}

/** Whether an item is withdrawn for the client: its condition is `#never`. Pure, and covered under `jsNodeTest`. */
fun menuItemHidden(item: MenuItemView): Boolean = item.condition == CFACT.neverName

/** What an audience with no name reads as (issue #1094): a condition somebody wrote by hand, which the menu's table does not hold. */
const val customAudienceText = "Custom"

/** A condition as a reader is told it: "hidden" when it withdraws the item, else its [audience]'s name, else "Custom". */
private fun audienceWord(condition: String?, audience: String?): String =
    if (condition == CFACT.neverName) "hidden" else audience ?: customAudienceText

/**
 * How an item's visibility reads (issues #919, #1094): "hidden" when withdrawn, else **the audience's name** as the
 * backend gave it -- "Everyone signed in" -- and "Custom" for a condition that has none. With a note when the client
 * changed it from the shipped state ("hidden (shipped: Everyone)"), judged by the conditions, so two audiences are
 * never taken for one because they read alike; an item the client added has no shipped state, and no note. The
 * expression itself is the detail ([menuVisibilityDetail]). Pure, and covered under `jsNodeTest`.
 */
fun menuVisibilityText(item: MenuItemView): String {
    val now = audienceWord(item.condition, item.audience)
    return if (asShipped(item)) now else "$now (shipped: ${audienceWord(item.baseCondition, item.baseAudience)})"
}

/**
 * The detail behind [menuVisibilityText] (issue #1094): the condition as it is stored, which is what the name stands
 * for -- and the shipped one beside it when the client changed it. An item with no condition is drawn under
 * `#always`. Pure, and covered under `jsNodeTest`.
 */
fun menuVisibilityDetail(item: MenuItemView): String {
    val now = CFACT.orAlways(item.condition)
    return if (asShipped(item)) now else "$now (shipped: ${CFACT.orAlways(item.baseCondition)})"
}

/** Whether two conditions are one: no condition and `#always` both draw for everyone, on every node (`CFACT.orAlways`). */
private fun sameCondition(a: String?, b: String?): Boolean = CFACT.orAlways(a) == CFACT.orAlways(b)

/** Whether there is no shipped state to note beside [item]'s own: it is as shipped, or the client added it and nothing shipped. */
private fun asShipped(item: MenuItemView): Boolean = item.added || sameCondition(item.condition, item.baseCondition)

/**
 * The Show control's choices for [item] (issue #1094), each a label to the condition it sends: the audiences the
 * backend says the item may be shown to, by name, the one it shipped under marked so -- "Everyone (as shipped)" --
 * since putting an item back is the commonest reason to show one. Nothing is added or left out here: the backend's
 * list is what its write accepts. Pure, and covered under `jsNodeTest`.
 */
fun menuShowChoices(item: MenuItemView): List<Pair<String, String>> = item.audiences.map { choice ->
    (if (sameCondition(choice.condition, item.baseCondition)) "${choice.name} (as shipped)" else choice.name) to choice.condition
}

/** The items other items sit under -- the groups, which cannot be hidden (the bar would take their children too). Pure, and covered under `jsNodeTest`. */
fun menuGroups(items: List<MenuItemView>): Set<String> = items.mapNotNull { it.parentId }.toSet()

/**
 * The request that changes one item (issue #919): its address and the client, a new [label] when renaming, and the
 * [visibility] with its [condition] when hiding or showing -- and how the page said it would take effect
 * ([expected], from [expectedMode]). Pure, and covered under `jsNodeTest`.
 */
fun menuEditRequest(clientId: String, itemId: String, label: String?, visibility: String?, condition: String?, expected: String): Map<String, Any?> =
    linkedMapOf<String, Any?>(COV.client to clientId, COV.itemId to itemId).also {
        if (label != null) it[MNU.label] = label
        if (visibility != null) it[MNU.visibility] = visibility
        if (condition != null) it[MNU.condition] = condition
        it[CPY.mode] = expected
    }

/** What a menu edit did (issue #919), and how it took effect -- live, or a draft its sandbox runs (issue #930). */
class MenuEditResult(
    val configName: String,
    val label: String?,
    val condition: String?,
    /** What [condition]'s audience is called (issue #1094); null for a withdrawn item or a condition with no name. */
    val audience: String? = null,
    val stored: Boolean,
    val issues: List<String>,
    val mode: String = EDM.live,
)

/** The set/reset result as a [MenuEditResult]. Pure, and covered under `jsNodeTest`. */
fun parseMenuEditResult(results: Map<String, Any?>): MenuEditResult = MenuEditResult(
    configName = results[COV.configName].toOptStr().orEmpty(),
    label = results[MNU.label].toOptStr(),
    condition = results[MNU.condition].toOptStr(),
    audience = results[MNU.audience].toOptStr(),
    stored = results[CPY.stored] == true,
    issues = results[CPY.issues].toJsonListOfMaps().mapNotNull { it[GCI.message].toOptStr() },
    mode = results[CPY.mode].toOptStr() ?: EDM.live,
)

/** What the note says once an item is shown (issue #1094): who to, by the audience's name. Pure, and covered under `jsNodeTest`. */
fun menuShownNote(itemId: String, result: MenuEditResult): String =
    "$itemId is now offered to: ${result.audience ?: customAudienceText}."

/** One configuration issue as the detail lists it (issue #906): what is wrong, what was dropped, and where it came from. */
class ConfigIssueView(val message: String, val degradedTo: String, val origin: String)

/**
 * One client's definition as the detail view shows it (issue #906): its attributes as `ClientInfo` writes them
 * ([info], read by the `CLD` keys), whether this node carries it, its issues, and what it defines -- supported
 * trait ids, listing-column labels, and workflow ids.
 */
class ClientDefinitionView(
    val info: Map<String, Any?>,
    /** The client's stored definition as its latest revision holds it (issue #1026), or null when it has none. */
    val stored: Map<String, Any?>?,
    /** With [stored]: the name of the stored configuration holding it. */
    val storedConfig: String?,
    val present: Boolean,
    val issues: List<ConfigIssueView>,
    val traitIds: List<String>,
    val usageLabels: List<String>,
    val workflowIds: List<String>,
    /**
     * The deployment's values of the fields a client may leave unset to take them (issue #1308), by [CLD] key -- the
     * session lifetime and the refresh interval -- so the page can say what an unset one means. Empty on a read that
     * carries none.
     */
    val platformDefaults: Map<String, Int> = emptyMap(),
)

/** One stored configuration as the detail lists it (issue #906): a row of the config bundles listing. */
class ConfigSummaryView(
    val name: String,
    val version: Int,
    val published: Boolean,
    val publishedAt: String?,
    val updatedAt: String?,
    val issueCount: Int,
    /** The latest published revision's version (issue #1001), or null when none is. */
    val publishedVersion: Int? = null,
    /** How many slots, and entries in all, the latest revision holds (issue #1461). */
    val slotCount: Int = 0,
    val entryCount: Int = 0,
    /** How many slots, and entries in all, the latest published revision holds; null when none is. */
    val publishedSlotCount: Int? = null,
    val publishedEntryCount: Int? = null,
)

/** The issues of a definition or a stored configuration, as the wire carries them. */
private fun parseConfigIssues(raw: Any?): List<ConfigIssueView> = raw.toJsonListOfMaps().mapNotNull { issue ->
    val message = issue[GCI.message].toOptStr() ?: return@mapNotNull null
    ConfigIssueView(message, issue[GCI.degradedTo].toOptStr().orEmpty(), issue[GCI.origin].toOptStr().orEmpty())
}

/** The definition item as a [ClientDefinitionView]. Pure, and covered under `jsNodeTest`. */
fun parseClientDefinition(item: Map<String, Any?>): ClientDefinitionView = ClientDefinitionView(
    info = item[CLD.client].toJsonMapOrEmpty(),
    stored = (item[CLD.storedDefinition] as? Map<*, *>)?.let { item[CLD.storedDefinition].toJsonMapOrEmpty() },
    storedConfig = item[CLD.storedDefinitionConfig].toOptStr(),
    present = item[CLD.present] == true,
    issues = parseConfigIssues(item[CLD.issues]),
    traitIds = item[CLD.traits].toJsonListOfMaps().mapNotNull { it[CCT.traitId].toOptStr() },
    usageLabels = item[CLD.usages].toJsonListOfMaps().mapNotNull { it[UF.label].toOptStr() },
    workflowIds = item[CLD.workflows].toJsonListOfStrings(),
    platformDefaults = item[CLD.platformDefaults].toJsonMapOrEmpty().mapNotNull { (k, v) -> (v as? Number)?.let { k to it.toInt() } }.toMap(),
)

/** The config bundles listing's items as [ConfigSummaryView]s; one without a name is not a configuration. Pure, and covered under `jsNodeTest`. */
fun parseConfigSummaries(items: List<Map<String, Any?>>): List<ConfigSummaryView> = items.mapNotNull { row ->
    ConfigSummaryView(
        name = row[CFEP.name].toOptStr() ?: return@mapNotNull null,
        version = (row[CFEP.version] as? Number)?.toInt() ?: 0,
        published = row[CFEP.published] == true,
        publishedAt = row[CFEP.publishedAt].toOptStr(),
        updatedAt = row[CFEP.updatedAt].toOptStr(),
        issueCount = row[CFEP.issues].toJsonListOfMaps().size,
        publishedVersion = (row[CFEP.publishedVersion] as? Number)?.toInt(),
        slotCount = (row[CFEP.slotCount] as? Number)?.toInt() ?: 0,
        entryCount = (row[CFEP.entryCount] as? Number)?.toInt() ?: 0,
        publishedSlotCount = (row[CFEP.publishedSlotCount] as? Number)?.toInt(),
        publishedEntryCount = (row[CFEP.publishedEntryCount] as? Number)?.toInt(),
    )
}

/** The note beside an operator-only attribute (issue #820) for an administrator who may not change it. */
const val platformSetNote = "(set by the platform)"

/** The note beside a platform attribute the definition editor does not offer, for an administrator who sees every client. */
const val configurationSetNote = "(set in its configuration)"

/**
 * Who sets [field], said beside its read-only value (issue #1116), or null when the caller edits it here: the
 * platform's, for a client's own administrator ([platformSetNote]); and for an administrator who sees every client,
 * a platform field the editor does not offer -- the customer, the template -- which a configuration write sets
 * ([configurationSetNote]), and the static lock and the web resources package (issue #1301), which only source code
 * sets ([sourceSetNote]). Read from the field's
 * policy, so the editor and the notes cannot disagree. Pure, and covered under `jsNodeTest`.
 */
fun whoSetsNote(field: String, canSeeAllClients: Boolean): String? {
    if (ClientFieldPolicies.of(field).edit != ClientFieldEdit.platform) return null
    return when {
        !canSeeAllClients -> platformSetNote
        field in ClientFieldPolicies.platformEditorFields -> null
        field == CLD.staticConfig || field == CLD.webResourcesId -> sourceSetNote
        else -> configurationSetNote
    }
}

/** The note beside the static lock and the web resources package for an administrator who sees every client: only source code sets them. */
const val sourceSetNote = "(set in source code)"

/** The two counts with a listing behind them, which the header links to ([clientHeaderLinks]); see [clientSummaryHref]. */
const val summaryFormsRow = "Forms"
const val summaryUsersRow = "Users"

/** The summary row naming the client's customer (issue #1114): its label, as the summary draws it. */
val summaryCustomerRow: String = humanizeFieldName(CLD.customerId)

/**
 * Where a row of the client's summary leads, or null for one that is only a fact (issue #1082): the Forms and Users
 * counts open the listings behind them, exactly as the same counts do in the Clients listing ([clientFormsHref],
 * [clientUsersHref]) -- for a client this node carries, which is where those pages work. An administrator of one
 * client lands on this summary and never sees the listing, so without this the counts would lead nowhere for the
 * one caller who used to follow them. The customer row opens the client's customer ([customerSummaryHref], issue
 * #1114) when the caller may see it. Pure, and covered under `jsNodeTest`.
 */
fun clientSummaryHref(label: String, row: ClientOverview?, acrossClients: Boolean, customerId: String? = null): String? {
    if (label == summaryCustomerRow) return customerSummaryHref(customerId, row, acrossClients)
    if (row == null || row.status != ClientStatus.present.name) return null
    return when (label) {
        summaryFormsRow -> clientFormsHref(row.clientId, acrossClients)
        summaryUsersRow -> clientUsersHref(row.clientId, acrossClients)
        else -> null
    }
}

/**
 * Where the client summary's customer row leads (issue #1114): to the customer's page, exactly when the caller may
 * open it -- always for an administrator who sees every client, and otherwise only on the page of the customer's
 * root client ([ClientOverview.rootOf]), whose administrators act for the customer. Null elsewhere, where the page
 * would only refuse: an administrator of another of the customer's clients sees its id as plain text. Pure, and
 * covered under `jsNodeTest`.
 */
fun customerSummaryHref(customerId: String?, row: ClientOverview?, acrossClients: Boolean): String? = when {
    customerId == null -> null
    acrossClients || row?.rootOf == customerId -> customerHref(customerId)
    else -> null
}

/**
 * What the definition editor says of the session lifetime and the refresh interval (issue #1308): blank takes the
 * platform's, and a lifetime is at most the platform's limit.
 */
const val numberFieldsEditNote = "A blank session lifetime or refresh interval is the platform's, as its placeholder " +
    "says. A session lifetime may be at most the platform's limit; shortening it ends longer sessions at once, and " +
    "lengthening it reaches sign-ins made after."

/** A number of hours as read: whole days as days (`30 days`, `1 day`), anything else as hours (`8 hours`). Pure. */
fun hoursText(hours: Int): String = when {
    hours % 24 == 0 -> (hours / 24).let { if (it == 1) "1 day" else "$it days" }
    hours == 1 -> "1 hour"
    else -> "$hours hours"
}

/** A number of seconds as read: whole minutes as minutes (`2 minutes`), anything else as seconds (`45 seconds`). Pure. */
fun secondsText(seconds: Int): String = when {
    seconds % 60 == 0 -> (seconds / 60).let { if (it == 1) "1 minute" else "$it minutes" }
    seconds == 1 -> "1 second"
    else -> "$seconds seconds"
}

/** A value of the number field [name] as read: [hoursText] for the session lifetime, [secondsText] for the refresh interval. */
fun numberFieldText(name: String, value: Int): String =
    if (name == CLD.sessionHours) hoursText(value) else secondsText(value)

/**
 * What the platform default of [name] is said as ("Platform default: 2 days"), with the session lifetime's limit when
 * [defaults] has it ("Platform default: 2 days; at most 30 days"), or null when [defaults] has no default. Pure.
 */
fun platformDefaultText(name: String, defaults: Map<String, Int>): String? = defaults[name]?.let { value ->
    val most = defaults[CLD.maxSessionHours]?.takeIf { name == CLD.sessionHours }?.let { "; at most ${hoursText(it)}" }.orEmpty()
    "Platform default: ${numberFieldText(name, value)}$most"
}

/**
 * The value of [name] in force for a client whose definition is [info] (issue #1308): its own, or -- when it sets
 * none -- the platform's from [defaults], said as such ("30 days (platform default)"); a dash when neither is known.
 * Pure, and covered under `jsNodeTest`.
 */
fun inForceText(name: String, info: Map<String, Any?>, defaults: Map<String, Int>): String {
    (info[name] as? Number)?.let { return numberFieldText(name, it.toInt()) }
    return defaults[name]?.let { "${numberFieldText(name, it)} (platform default)" } ?: "\u2014"
}

// --- editing the definition (issue #1026) ---------------------------------------------------------------------

/**
 * What the definition editor says of the time zone (issues #1115, #1257): how to find one, and that changing it moves
 * nothing already stored.
 */
const val timeZoneEditNote = "Type a city, a zone's name, or an offset such as -5 to find a time zone beyond the " +
    "common ones; cleared, the client's is the deployment's. " +
    "Changing it moves no stored moment: a time already saved stays that time, shown in the new zone."

/**
 * The draft the editor opens on (issues #1026, #1116): each of `ClientPresentationFields`, the platform's included, as
 * text -- the labels and the environments as one comma-separated line, the preload as `true` or `false` -- seeded from
 * [baseline], the client's **stored** definition (`storedDefinition`), an absent field blank. The stored one, not what the client runs: those differ by an unpublished draft, which the editor must
 * show and be able to take back, and by what a template fills in, which written back would become the client's own.
 * Pure, and covered under `jsNodeTest`.
 */
fun definitionDraftOf(baseline: Map<String, Any?>): Map<String, String> =
    ClientPresentationFields.namesFor(platform = true).associateWith { name ->
        when (ClientPresentationFields.kind(name)) {
            ClientPresentationFields.Kind.labels, ClientPresentationFields.Kind.choices ->
                baseline[name].toJsonListOfStrings().joinToString(", ")
            ClientPresentationFields.Kind.flag -> (baseline[name] == true).toString()
            else -> baseline[name].toOptStr().orEmpty()
        }
    }

/** The values a draft's comma-separated [text] names for the [Kind.choices][ClientPresentationFields.Kind.choices] field [name], in the choices' order. */
fun choicesOfText(name: String, text: String): List<String> {
    val named = text.split(',').map { it.trim() }.toSet()
    return ClientPresentationFields.choicesOf(name).filter { it in named }
}

/** The labels a comma-separated line names, each once and trimmed -- the rule a client's list is held to. */
fun labelsOfText(text: String): List<String> = normalizeUserLabels(text.split(','))

/**
 * The request that saves [draft] for [clientId]: the client, and **only the fields that differ** from [baseline],
 * the stored definition the draft was seeded from -- a text field trimmed, a cleared one sent blank (the backend
 * clears it), the labels and the environments as lists, the preload as a flag -- of the fields the editor offers its
 * caller ([platform]: an administrator who sees every client, issue #1116) -- and how the page said the save would
 * take effect ([expected], from [expectedMode]). Naming no field when nothing changed, which is when Save has nothing to do
 * ([definitionEditChanges]). Pure, and covered under `jsNodeTest`.
 */
fun definitionEditRequest(
    clientId: String, baseline: Map<String, Any?>, draft: Map<String, String>, expected: String, platform: Boolean = false,
): Map<String, Any?> {
    val out = linkedMapOf<String, Any?>(CLD.client to clientId, CPY.mode to expected)
    for (name in ClientPresentationFields.namesFor(platform)) {
        when (ClientPresentationFields.kind(name)) {
            ClientPresentationFields.Kind.labels -> {
                val labels = labelsOfText(draft[name].orEmpty())
                if (labels != baseline[name].toJsonListOfStrings()) out[name] = labels
            }
            ClientPresentationFields.Kind.choices -> {
                val chosen = choicesOfText(name, draft[name].orEmpty())
                if (chosen.toSet() != baseline[name].toJsonListOfStrings().toSet()) out[name] = chosen
            }
            ClientPresentationFields.Kind.flag -> {
                val on = draft[name] == "true"
                if (on != (baseline[name] == true)) out[name] = on
            }
            else -> {
                val now = draft[name].orEmpty().trim()
                if (now != baseline[name].toOptStr().orEmpty()) out[name] = now
            }
        }
    }
    return out
}

/** Whether a [definitionEditRequest] asks for anything: whether it names any field. */
fun definitionEditChanges(request: Map<String, Any?>): Boolean = ClientPresentationFields.namesFor(platform = true).any { it in request }

/**
 * Whether the detail offers the definition editor -- and the Traits and Workflows sections beside it -- for the client
 * [row] describes (issue #1026): one this node carries, defined in **stored** configuration (a source definition is
 * edited in source), and not static here (a static client takes nothing stored). A **sandbox** is offered them too
 * (issue #1358): it holds no configuration, so a save from its page is a draft of its parent's, which it runs -- and
 * whether that is stored is said by the parent's stored definition, which the page is handed for it. The backend
 * refuses each of these in its own words; this only spares showing an editor that cannot save. Pure, and covered
 * under `jsNodeTest`.
 */
fun definitionEditable(row: ClientOverview?): Boolean =
    row != null && row.status == ClientStatus.present.name && !row.staticHere &&
        (row.origin == GedraConfigOrigin.stored.name || row.sandboxOf != null)

/** What the detail offers for editing the definition (issue #1026): the [editor], a [note] in its place, or neither yet. */
class DefinitionEditOffer(val editor: Boolean, val note: String?)

/**
 * Whether the detail offers the definition editor (issue #1026), or a note in its place, or neither yet: the
 * stored configuration [storedConfig] holding the definition may have unpublished changes, which a save would publish
 * unseen -- the backend's draft rule -- so it must be published first, and the page says so before the attempt.
 * Not where a save is a draft ([sandbox]: the page's [pageSavesDraft] -- a client whose definition asks for a sandbox,
 * or a sandbox's own page, issue #1358 -- which is what the backend reads to make a save a draft, and a draft publishes
 * nothing unseen); and nothing is offered until the [configs] have loaded, so an editor is
 * never opened and then replaced by the note. Pure, and covered under `jsNodeTest`.
 */
fun definitionEditOffer(storedConfig: String?, configs: List<ConfigSummaryView>?, sandbox: Boolean): DefinitionEditOffer {
    if (storedConfig == null || configs == null) return DefinitionEditOffer(editor = false, note = null)
    val holder = configs.firstOrNull { it.name == storedConfig }
    if (sandbox || holder == null || holder.published) return DefinitionEditOffer(editor = true, note = null)
    return DefinitionEditOffer(
        editor = false,
        note = "This client's definition is in configuration '$storedConfig', which has unpublished changes that a " +
            "save would publish with it. Publish it first",
    )
}

/** Whether a configuration row wants attention in the table (issue #1026): it could be published from this page (#1353). */
fun configRowNeedsPublish(row: ClientOverview?, bundle: ConfigSummaryView): Boolean = bundleAction(row, bundle).publish

/** What a definition edit did (issue #1026), and how it took effect -- live, or a draft its sandbox runs (issue #930). */
class DefinitionEditResult(val configName: String, val info: Map<String, Any?>, val issues: List<String>, val mode: String = EDM.live)

/** The set result as a [DefinitionEditResult]. Pure, and covered under `jsNodeTest`. */
fun parseDefinitionEditResult(results: Map<String, Any?>): DefinitionEditResult = DefinitionEditResult(
    configName = results[COV.configName].toOptStr().orEmpty(),
    info = results[CLD.definition].toJsonMapOrEmpty(),
    issues = results[CPY.issues].toJsonListOfMaps().mapNotNull { it[GCI.message].toOptStr() },
    mode = results[CPY.mode].toOptStr() ?: EDM.live,
)

/** The clients endpoints (issue #905): the scoped surface, which both kinds of administrator reach. */
object ClientsApi {
    /** The clients this administrator oversees, as the overview lists them. */
    suspend fun listOverview(): List<ClientOverview> =
        parseClientOverview(Http.getApi(UADEP.clientsOverview)[EP.items].toJsonListOfMaps())

    /** One client's definition (issue #906), through the scoped retrieve: the caller's own, or one they may name. */
    suspend fun definition(clientId: String): ClientDefinitionView =
        parseClientDefinition(Http.getApi(UADEP.clientDefinition + queryString(mapOf(CLD.client to clientId)))[EP.item].toJsonMapOrEmpty())

    /** Retires [clientId], or brings it back (issue #1182); the platform's switch (#1111), live at once. */
    suspend fun setRetired(clientId: String, retired: Boolean) {
        Http.sendApi("POST", ADEP.clientRetirementSet, mapOf(CLD.client to clientId, CLD.retired to retired))
    }

    /**
     * Adds or removes [clientId]'s sandbox (issue #932): the flag published and the client reloaded, so the sandbox is
     * there -- or gone -- when this returns. Returns whether it now has one.
     */
    suspend fun setSandbox(clientId: String, on: Boolean): Boolean =
        Http.sendApi("POST", UADEP.clientSandbox, mapOf(CLD.client to clientId, CLD.sandbox to on))[EP.results]
            .toJsonMapOrEmpty()[CLD.sandbox] == true

    /**
     * Recalculates the stored state of [clientId]'s forms now (issue #1412), synchronously: answers what it did, or
     * that another recalculation of them was running. Refused (400) for a client holding more forms than its limit.
     */
    suspend fun recalculateFormStates(clientId: String): FormStatesRecalc =
        parseFormStatesRecalc(Http.sendApi("POST", UADEP.clientFormStatesRecalc, mapOf(CLD.client to clientId))[EP.results].toJsonMapOrEmpty())

    /** Every slot of [clientId]'s web assets (issue #1130), each with the metadata of the image it holds. */
    suspend fun webAssets(clientId: String): List<WebAssetSlot> =
        parseWebAssetSlots(Http.getApi(UADEP.clientWeb + queryString(mapOf(WAS.client to clientId)))[EP.items].toJsonListOfMaps())

    /**
     * Uploads [file] -- the browser's own `File`, from a picker -- as the image of [slot] of [clientId]'s web assets
     * (issue #1130), replacing the one there. Live for everyone in the client when this returns, which answers the slot
     * as it now is; the backend refuses a file that is not the image it says it is, in its own words.
     */
    suspend fun uploadWebAsset(clientId: String, slot: String, file: Any): WebAssetSlot? =
        webAssetSlotOf(Http.uploadApi(UADEP.clientWebUpload, mapOf(WAS.client to clientId, WAS.slot to slot, WAS.file to file)))

    /**
     * Empties [slot] of [clientId]'s web assets (issue #1130), so its people see the deployment's image again.
     * Like the upload, answers the slot as it now is.
     */
    suspend fun removeWebAsset(clientId: String, slot: String): WebAssetSlot? =
        webAssetSlotOf(Http.sendApi("POST", UADEP.clientWebRemove, mapOf(WAS.client to clientId, WAS.slot to slot)))

    /**
     * [clientId]'s file refs (issue #1176) as its administrator is shown them: for a client with a sandbox the
     * sandbox's, drafts included, each saying whether the client's own people are served it yet.
     */
    suspend fun fileRefs(clientId: String): List<FileRefView> =
        parseFileRefs(Http.getApi(UADEP.clientFiles + queryString(mapOf(FREF.client to clientId)))[EP.items].toJsonListOfMaps())

    /** What a file ref takes -- size, kinds, how many, and the roots open to clients -- before one is offered. */
    suspend fun fileRefTakes(clientId: String): FileRefTakes =
        parseFileRefTakes(Http.getApi(UADEP.clientFilesTakes + queryString(mapOf(FREF.client to clientId)))[EP.item].toJsonMapOrEmpty())

    /**
     * Uploads [file] -- the browser's own `File` -- as [clientId]'s ref [refId], replacing what that ref named.
     * [description] null keeps the ref's description. Live, or a draft for a client with a sandbox, which the
     * answer says; the backend refuses a file that is not the image it says it is, in its own words.
     *
     * [expected] is how the page told its user the change would take effect ([expectedMode]), sent with every
     * change: one the page called a draft that would be live is refused, and not made.
     */
    suspend fun uploadFileRef(clientId: String, refId: String, file: Any, description: String?, expected: String): FileRefChange =
        fileRefChangeOf(
            Http.uploadApi(
                UADEP.clientFilesUpload,
                buildMap {
                    put(FREF.client, clientId)
                    put(CCT.refId, refId)
                    put(FREF.file, file)
                    description?.let { put(CCT.description, it) }
                    put(FREF.mode, expected)
                },
            ),
        )

    /**
     * Names the file at [path], in [clientId]'s own directory of the root [root], as its ref [refId] (issue #1164):
     * a file the deployment keeps for the client, in a root it opened to clients. Nothing is uploaded.
     */
    suspend fun nameLocalFileRef(
        clientId: String, refId: String, root: String, path: String, description: String?, expected: String,
    ): FileRefChange =
        fileRefChangeOf(
            Http.sendApi(
                "POST", UADEP.clientFilesLocal,
                buildMap {
                    put(FREF.client, clientId)
                    put(CCT.refId, refId)
                    put(CCT.root, root)
                    put(CCT.path, path)
                    description?.let { put(CCT.description, it) }
                    put(FREF.mode, expected)
                },
            ),
        )

    /** Removes [clientId]'s ref [refId]; the answer holds the ref still there under that id, when one was shipped. */
    suspend fun removeFileRef(clientId: String, refId: String, expected: String): FileRefChange =
        fileRefChangeOf(
            Http.sendApi("POST", UADEP.clientFilesRemove, mapOf(FREF.client to clientId, CCT.refId to refId, FREF.mode to expected)),
        )

    /**
     * Puts back, in [clientId]'s draft, the ref [refId] a draft removed (issue #1188): the entry its people are served,
     * as it is published. Only for a ref the listing marks removed in a draft.
     */
    suspend fun restoreFileRef(clientId: String, refId: String, expected: String): FileRefChange =
        fileRefChangeOf(
            Http.sendApi("POST", UADEP.clientFilesRestore, mapOf(FREF.client to clientId, CCT.refId to refId, FREF.mode to expected)),
        )

    /**
     * Makes [description] what [clientId]'s ref [refId] says it is for, a blank one clearing it; the file is as it
     * was. Only for a ref a stored configuration holds.
     */
    suspend fun describeFileRef(clientId: String, refId: String, description: String, expected: String): FileRefChange =
        fileRefChangeOf(
            Http.sendApi(
                "POST", UADEP.clientFilesDescribe,
                mapOf(FREF.client to clientId, CCT.refId to refId, CCT.description to description, FREF.mode to expected),
            ),
        )

    private fun fileRefChangeOf(envelope: Map<String, Any?>): FileRefChange = parseFileRefChange(envelope[EP.results].toJsonMapOrEmpty())

    /** The one slot a change answers under `results`, or null from a backend that answered none. */
    private fun webAssetSlotOf(envelope: Map<String, Any?>): WebAssetSlot? =
        parseWebAssetSlots(listOf(envelope[EP.results].toJsonMapOrEmpty())).firstOrNull()

    /**
     * Saves a client's definition edit (issue #1026) -- [request] from [definitionEditRequest] -- and makes it take
     * effect: live, or a draft for a client with a sandbox; the backend refuses what its trial faults.
     */
    suspend fun setDefinition(request: Map<String, Any?>): DefinitionEditResult =
        parseDefinitionEditResult(Http.sendApi("POST", UADEP.clientDefinitionSet, request)[EP.results].toJsonMapOrEmpty())

    /** The global traits a client may include (issue #1117), as the platform declares them. */
    suspend fun traitCatalog(clientId: String): TraitCatalogView =
        parseTraitCatalog(Http.getApi(UADEP.clientTraits + queryString(mapOf(CLD.client to clientId)))[EP.item].toJsonMapOrEmpty())

    /**
     * Saves the traits a client includes (issue #1117) -- [request] from [traitsEditRequest] -- live, or a draft for a
     * client with a sandbox. A live save taking away a trait its forms hold is refused with the impact report
     * ([impactOf]) until acknowledged.
     */
    suspend fun setIncludedTraits(request: Map<String, Any?>): TraitsSetResult =
        parseTraitsSetResult(Http.sendApi("POST", UADEP.clientTraitsSet, request)[EP.results].toJsonMapOrEmpty())

    /**
     * Saves a trait's design (issue #1118) -- [request] from [traitDesignRequest]: a new trait, or an edit of one the
     * client stores; live, or a draft for a client with a sandbox. An edit that strands stored data is refused with
     * the impact report ([impactOf]) until acknowledged.
     */
    suspend fun saveTraitDesign(request: Map<String, Any?>): TraitDesignResult =
        parseTraitDesignResult(Http.sendApi("POST", UADEP.clientTraitDesign, request)[EP.results].toJsonMapOrEmpty())

    /**
     * Saves a client's narrowing of a global trait (issue #1119) -- [request] from [traitNarrowRequest]; live, or a draft
     * for a client with a sandbox. One that strands stored data is refused with the impact report ([impactOf]).
     */
    suspend fun narrowTrait(request: Map<String, Any?>): TraitNarrowResult =
        parseTraitNarrowResult(Http.sendApi("POST", UADEP.clientTraitNarrow, request)[EP.results].toJsonMapOrEmpty())

    /** A client's workflows and the templates a workflow can be made from (issue #1120). */
    suspend fun workflowCatalog(clientId: String): WorkflowCatalogView =
        parseWorkflowCatalog(Http.getApi(UADEP.clientWorkflows + queryString(mapOf(CLD.client to clientId)))[EP.item].toJsonMapOrEmpty())

    /**
     * Makes a workflow from a template, or changes the slots of one (issue #1120) -- [request] from
     * [workflowSaveRequest]; live, or a draft for a client with a sandbox. One that affects stored forms is refused with
     * the impact report ([impactOf]).
     */
    suspend fun saveWorkflow(request: Map<String, Any?>): WorkflowSaveResult =
        parseWorkflowSaveResult(Http.sendApi("POST", UADEP.clientWorkflowSave, request)[EP.results].toJsonMapOrEmpty())

    /**
     * Removes one of a client's workflows made from a template (issue #1346) -- [request] from [workflowRemoveRequest];
     * live, or a draft for a client with a sandbox. One forms take part in is refused with the impact report ([impactOf]).
     */
    suspend fun removeWorkflow(request: Map<String, Any?>): WorkflowRemoveResult =
        parseWorkflowRemoveResult(Http.sendApi("POST", UADEP.clientWorkflowRemove, request)[EP.results].toJsonMapOrEmpty())

    /**
     * Sets one of a client's workflows' parameters (issues #1121, #1356, #1384) -- [request] from [windowsSaveRequest]
     * or [visibilitySaveRequest] -- in force at once in the client or sandbox named. One that would freeze or hide
     * engaged forms now, or take one out of its owner's sight, is refused with the impact report ([impactOf]).
     */
    suspend fun saveWorkflowParams(request: Map<String, Any?>): WorkflowParamsView =
        parseWindowsSaveResult(Http.sendApi("POST", UADEP.clientWorkflowParams, request)[EP.results].toJsonMapOrEmpty())

    /** A client's listing columns and the traits a column may read (issue #1491): the client's own, or one named. */
    suspend fun usageCatalog(clientId: String): UsageCatalogView =
        parseUsageCatalog(Http.getApi(UADEP.clientUsages + queryString(mapOf(CLD.client to clientId)))[EP.item].toJsonMapOrEmpty())

    /**
     * Adds a listing column, or changes one where it is stored (issue #1491) -- [request] from [usageSaveRequest]; live,
     * or a draft for a client with a sandbox.
     */
    suspend fun saveUsage(request: Map<String, Any?>): UsageSaveResult =
        parseUsageSaveResult(Http.sendApi("POST", UADEP.clientUsageSave, request)[EP.results].toJsonMapOrEmpty())

    /**
     * Removes a listing column where it is stored (issue #1491) -- [request] from [usageRemoveRequest]. Answers the
     * configuration it was in, and how it took effect.
     */
    suspend fun removeUsage(request: Map<String, Any?>): Pair<String, String> =
        Http.sendApi("POST", UADEP.clientUsageRemove, request)[EP.results].toJsonMapOrEmpty().let {
            it[CIT.configName].toOptStr().orEmpty() to it[CPY.mode].toOptStr().orEmpty()
        }

    /** Every key an administrator may override for a client, with the client's value (issue #918). */
    suspend fun copyKeys(clientId: String): List<CopyKeyView> =
        parseCopyKeys(Http.getApi(CPY.keysPath + queryString(mapOf(COV.client to clientId)))[EP.items].toJsonListOfMaps())

    /**
     * Saves the pending changes to one file of a client's copy together (issue #1062) -- [order] being the file's keys
     * as the view lists them -- and makes them live, or a draft for a client with a sandbox; the backend refuses a
     * batch any of whose values its trial faults, and then nothing changes.
     */
    suspend fun applyCopy(clientId: String, pending: Map<CopyAddress, PendingCopy>, order: List<CopyAddress>, expected: String): CopyApplyResult =
        parseCopyApplyResult(Http.sendApi("POST", CPY.applyPath, copyApplyRequest(clientId, pending, order, expected))[EP.results].toJsonMapOrEmpty())

    /** The home menu's items for a client (issue #919): shipped and effective label and condition. */
    suspend fun menuItems(clientId: String): List<MenuItemView> =
        parseMenuItems(Http.getApi(MNU.itemsPath + queryString(mapOf(COV.client to clientId)))[EP.items].toJsonListOfMaps())

    /** Renames, hides or shows one item for a client and makes it live (issue #919). */
    suspend fun setMenuItem(clientId: String, itemId: String, label: String?, visibility: String?, condition: String?, expected: String): MenuEditResult =
        parseMenuEditResult(Http.sendApi("POST", MNU.setPath, menuEditRequest(clientId, itemId, label, visibility, condition, expected))[EP.results].toJsonMapOrEmpty())

    /** Removes a client's stored changes to one item and makes that live (issue #919). */
    suspend fun resetMenuItem(clientId: String, itemId: String, expected: String): MenuEditResult =
        parseMenuEditResult(Http.sendApi("POST", MNU.resetPath, menuEditRequest(clientId, itemId, null, null, null, expected))[EP.results].toJsonMapOrEmpty())

    /** What one client's own configuration changes (issue #917), through the scoped retrieve. */
    suspend fun overrides(clientId: String): ClientOverridesView =
        parseClientOverrides(Http.getApi(UADEP.clientOverrides + queryString(mapOf(COV.client to clientId)))[EP.item].toJsonMapOrEmpty())

    /**
     * The stored configurations this node holds for a client (issue #906), from the listing [storedConfigsPath]
     * names -- or none, when there is no listing this caller may ask about that client.
     */
    suspend fun storedConfigs(clientId: String, acrossClients: Boolean, ownClient: String): List<ConfigSummaryView> {
        val path = storedConfigsPath(clientId, acrossClients, ownClient) ?: return emptyList()
        return parseConfigSummaries(Http.getApi(path)[EP.items].toJsonListOfMaps())
    }

    /**
     * Publishes [clientId]'s configurations [names] together and reloads the client (issues #1001, #1352), so a
     * published-only client runs what was just published. For a sandbox, its parent's: the full-scope surface names the parent; the scoped
     * one, called in the sandbox, acts on the parent already (#930). A publish that would affect the client's stored
     * data is refused unless [acknowledgeImpact] (issue #935); [impactOf] reads the report off that refusal.
     */
    suspend fun publishBundles(clientId: String, names: List<String>, acrossClients: Boolean, acknowledgeImpact: Boolean = false) {
        val request = publishBundleRequest(clientId, names, acrossClients, acknowledgeImpact)
        Http.sendApi("POST", request.path, request.body)
        if (acrossClients) {
            Http.sendApi("POST", ACEP.reload, mapOf(CFEP.client to (sandboxParentOf(clientId) ?: clientId)))
        } else {
            Http.sendApi("POST", CFEP.reload, emptyMap())
        }
    }

    /** Deletes [clientId]'s empty configuration [name], as it was read at [expectUpdatedAt], and reloads (issue #1461). */
    suspend fun deleteBundle(clientId: String, acrossClients: Boolean, name: String, expectUpdatedAt: Any?) {
        val request = bundleDeleteRequest(clientId, acrossClients, name, expectUpdatedAt)
        Http.sendApi("POST", request.path, request.body)
    }

    /**
     * Everything the bundle editor page draws for [clientId] (issue #1436), from the read [bundleEditorPath] names -- or
     * null, when there is none this caller may ask about that client.
     */
    suspend fun bundleEditor(clientId: String, acrossClients: Boolean, ownClient: String): BundleEditorView? {
        val path = bundleEditorPath(clientId, acrossClients, ownClient) ?: return null
        return parseBundleEditor(Http.getApi(path)[EP.item].toJsonMapOrEmpty())
    }

    /** What publishing [clientId]'s configurations [names] together would do to the client's stored data (issues #935, #1352). */
    suspend fun bundleImpact(clientId: String, names: List<String>, acrossClients: Boolean): ImpactReportView {
        val path = if (acrossClients) {
            ACEP.bundleImpact + queryString(mapOf(CFEP.client to (sandboxParentOf(clientId) ?: clientId), CFEP.names to names))
        } else {
            CFEP.bundleImpact + queryString(mapOf(CFEP.names to names))
        }
        return parseImpactReport(Http.getApi(path)[EP.item].toJsonMapOrEmpty())
    }
}

/** A request's path and body. */
class ApiRequest(val path: String, val body: Map<String, Any?>)

/**
 * The publish of [clientId]'s configurations [names], together (issues #1001, #935, #1352): the full-scope surface naming the owner --
 * a sandbox's parent, since a sandbox holds none of its own -- or the scoped one; with [acknowledgeImpact] when the
 * caller has seen the impact report and goes ahead. Pure, and covered under `jsNodeTest`.
 */
fun publishBundleRequest(clientId: String, names: List<String>, acrossClients: Boolean, acknowledgeImpact: Boolean): ApiRequest {
    val body = buildMap {
        if (acrossClients) put(CFEP.client, sandboxParentOf(clientId) ?: clientId)
        put(CFEP.names, names)
        if (acknowledgeImpact) put(IMP.acknowledgeImpact, true)
    }
    return ApiRequest(if (acrossClients) ACEP.bundlePublish else CFEP.bundlePublish, body)
}

/**
 * The delete of [clientId]'s empty configuration [name] (issue #1461), saying when its latest revision was written as
 * read ([expectUpdatedAt]), so a bundle written since is not deleted; reloading the client. From across clients it
 * names the owner, a sandbox's parent. Pure.
 */
fun bundleDeleteRequest(clientId: String, acrossClients: Boolean, name: String, expectUpdatedAt: Any?): ApiRequest {
    val body = buildMap {
        if (acrossClients) put(CFEP.client, sandboxParentOf(clientId) ?: clientId)
        put(CFEP.name, name)
        put(CFEP.expectUpdatedAt, expectUpdatedAt)
        put(CFEP.reloadField, true)
    }
    return ApiRequest(if (acrossClients) ACEP.bundleDelete else CFEP.bundleDelete, body)
}

/** One finding of a publish impact report (issue #935): rows a publish would affect the same way. */
class ImpactFindingView(
    val kind: String,
    val traitId: String?,
    val workflowId: String?,
    val taskId: String?,
    val count: Int,
    val sampleIds: List<String>,
)

/**
 * What publishing configurations together would do to their client's stored data (issues #935, #1352): the [configs]
 * judged, the rows [scanned], whether the client stores [tooLarge] a number to examine, and the [findings] -- only
 * what the publish would break.
 */
class ImpactReportView(
    val client: String,
    /** Each configuration judged: its name and the version of the revision judged. */
    val configs: List<Pair<String, Int>>,
    val scanned: Int,
    val tooLarge: Boolean,
    val findings: List<ImpactFindingView>,
) {
    /** Whether a publish needs acknowledging: as the backend decides it. */
    val blocks: Boolean get() = tooLarge || findings.isNotEmpty()
}

/** A report as the wire carries it -- from the report endpoint, or a refused publish's `extraData`. Pure. */
fun parseImpactReport(raw: Map<String, Any?>): ImpactReportView = ImpactReportView(
    client = raw[IMP.client].toOptStr().orEmpty(),
    configs = raw[IMP.configs].toJsonListOfMaps().map { it[IMP.name].toOptStr().orEmpty() to ((it[IMP.version] as? Number)?.toInt() ?: 0) },
    scanned = (raw[IMP.scanned] as? Number)?.toInt() ?: 0,
    tooLarge = raw[IMP.tooLarge] == true,
    findings = raw[IMP.findings].toJsonListOfMaps().map {
        ImpactFindingView(
            kind = it[IMP.kind].toOptStr().orEmpty(),
            traitId = it[IMP.traitId].toOptStr(),
            workflowId = it[IMP.workflowId].toOptStr(),
            taskId = it[IMP.taskId].toOptStr(),
            count = (it[IMP.count] as? Number)?.toInt() ?: 0,
            sampleIds = it[IMP.sampleIds].toJsonListOfStrings(),
        )
    },
)

/** The report a publish was refused over (issue #935), or null for any other error. Pure. */
fun impactOf(e: Throwable): ImpactReportView? {
    val api = e as? ApiError ?: return null
    if (api.errorCode != IMP.refusedCode) return null
    return parseImpactReport(api.extraData[IMP.report].toJsonMapOrEmpty())
}

/** The configurations a report judged, as a sentence names them ([bundleNamesText]). Pure. */
fun impactConfigsText(report: ImpactReportView): String = bundleNamesText(report.configs.map { it.first })

/** One finding as a sentence for the dialog (issue #935): how many forms, and what would happen to them. Pure. */
fun impactFindingText(f: ImpactFindingView): String {
    val one = f.count == 1
    val forms = if (one) "1 form" else "${f.count} forms"
    fun verb(single: String, plural: String) = if (one) single else plural
    return when (f.kind) {
        ImpactKind.traitGone.name ->
            "$forms ${verb("holds", "hold")} entries of trait ${f.traitId}, which the client would no longer support."
        ImpactKind.dataInvalid.name ->
            "$forms ${verb("holds", "hold")} entries of trait ${f.traitId} that would no longer be valid, and could " +
                "not be edited until they are fixed."
        ImpactKind.workflowGone.name ->
            "$forms ${verb("takes", "take")} part in workflow ${f.workflowId}, which would no longer exist."
        ImpactKind.stateStranded.name ->
            "$forms ${verb("is", "are")} at task ${f.taskId} of workflow ${f.workflowId}, which the workflow would " +
                "no longer define."
        ImpactKind.workflowFrozen.name ->
            "$forms ${verb("takes", "take")} part in workflow ${f.workflowId}, which would stop being tracked now: " +
                "${verb("its state", "their state")} would be kept as last calculated, read-only."
        ImpactKind.workflowHidden.name ->
            "$forms ${verb("takes", "take")} part in workflow ${f.workflowId}, which would no longer be available now: " +
                "nothing would show ${verb("its", "their")} part in it, though the data is kept."
        ImpactKind.workflowUnseen.name ->
            "$forms ${verb("takes", "take")} part in workflow ${f.workflowId}, which ${verb("its owner", "their owners")} " +
                "would no longer see: nothing would show them ${verb("its", "their")} part in it, though the progress is " +
                "kept for whoever still sees it."
        ImpactKind.valuesRecomputed.name ->
            "$forms ${verb("holds", "hold")} entries of trait ${f.traitId} whose computed values would change, at " +
                "${verb("its", "their")} next save or when the client's forms are recalculated."
        else -> "$forms would be affected (${f.kind})."
    }
}

/**
 * The sentence heading the impact dialog (issue #935): too many rows to examine, nothing found, or how many kinds of
 * harm were. [doing] names the change -- "Publishing <name>" unless a caller says otherwise, as Design View's save of
 * a removed choice does (issue #1040). Pure.
 */
fun impactSummary(report: ImpactReportView, doing: String = "Publishing ${impactConfigsText(report)}"): String = when {
    report.tooLarge -> "This client stores more forms than an impact check examines, so what " +
        "${doing.replaceFirstChar { it.lowercase() }} would do to them is unknown."
    // Exact about what was checked: the report looks for what would break, not for state a form would derive
    // differently, which is usually the point of the change.
    report.findings.isEmpty() -> "$doing would leave none of the client's " +
        "${if (report.scanned == 1) "1 stored form" else "${report.scanned} stored forms"} broken: no trait, field, " +
        "workflow or task they rely on goes away."
    else -> "$doing would affect forms ${report.client} already stores:"
}

/**
 * Which stored-configuration listing answers for [clientId] (issue #906): the full-scope one, naming the client,
 * for an administrator who sees across clients; the client-scoped one for the caller's **own** client -- it lists
 * that client whatever is asked, so it is asked only about it; and null for another client, whose page then shows
 * none rather than the caller's own under a foreign heading. Pure, and covered under `jsNodeTest`.
 */
fun storedConfigsPath(clientId: String, acrossClients: Boolean, ownClient: String): String? = when {
    // A sandbox holds no configuration of its own; it runs its parent's (issue #1001), so that is what it lists.
    acrossClients -> ACEP.bundles + queryString(mapOf(CFEP.client to (sandboxParentOf(clientId) ?: clientId)))
    // The scoped listing, called in a sandbox, lists its parent's already (#930).
    clientId == ownClient -> CFEP.bundles
    else -> null
}

/**
 * Whether a bundle's latest revision is live (issue #1001), given whether its client runs only published
 * configuration: published and live; a draft a published-only client does not run -- naming the version it does --
 * or one a client on its latest revision runs regardless. Pure, and covered under `jsNodeTest`.
 */
fun bundleLiveText(bundle: ConfigSummaryView, publishedOnly: Boolean): String = when {
    bundle.published -> "Live"
    !publishedOnly -> "Live, unpublished"
    bundle.publishedVersion != null -> "v${bundle.version} draft; v${bundle.publishedVersion} live"
    else -> "Draft; not live"
}

/**
 * What a bundle row offers (issue #1001): [publish] when publishing is offered, else a [note] saying why not, or what
 * publishing does -- and, where publishing belongs on the client's sandbox, [sandboxClient] to link to it.
 * [checkImpact] offers the publish impact report (issue #935) on a draft whose publishing would change what the
 * client runs: beside Publish, and on a client with a sandbox beside the note too, where the report's forms can be
 * opened (a session in the sandbox cannot read its parent's).
 */
class BundleAction(val publish: Boolean, val note: String?, val sandboxClient: String? = null, val checkImpact: Boolean = false)

/**
 * What the detail's configuration row for [bundle] offers, on the page of the client [row] (issue #1001). Nothing for
 * a published revision. A client static here takes nothing stored, so publishing means nothing. A client with a
 * sandbox publishes from the sandbox, after previewing, and the row points there. A sandbox publishes its parent's,
 * which is what it runs. A client without one publishes -- saying, on its latest revision, that this changes
 * nothing it runs. Pure, and covered under `jsNodeTest`.
 */
fun bundleAction(row: ClientOverview?, bundle: ConfigSummaryView): BundleAction = when {
    bundle.published -> BundleAction(false, null)
    row == null -> BundleAction(false, null)
    row.staticHere -> BundleAction(false, "This client takes nothing stored on this node, so publishing changes nothing.")
    row.hasSandbox -> BundleAction(
        false, "Publish from its sandbox, after previewing it there.", sandboxClient = sandboxOf(row.clientId), checkImpact = true,
    )
    row.sandboxOf != null -> BundleAction(true, "Publishing makes it live in ${row.sandboxParentName}.", checkImpact = true)
    // On its latest revision, publishing changes nothing it runs, so there is no impact to check.
    !row.publishedOnly -> BundleAction(true, "It runs its latest revision either way; publishing marks this one, and the next edit starts another.")
    else -> BundleAction(true, null, checkImpact = true)
}

/**
 * What the Stored configuration table offers for a **group** of bundles (issue #1353): rows select, and the bar above
 * acts on the selection. [BundleAction.publish] when this page publishes, [BundleAction.checkImpact] when a
 * selection's impact can be checked here (on a client with a sandbox, check only: it publishes from the sandbox), and
 * the [BundleAction.note] -- with [BundleAction.sandboxClient] to link to -- that once sat on every row. Read off any one draft's [bundleAction], since every
 * draft of one client is offered the same; null when nothing is unpublished. Pure, and covered under `jsNodeTest`.
 */
fun publishGroup(row: ClientOverview?, configs: List<ConfigSummaryView>): BundleAction? =
    configs.firstOrNull { !it.published }?.let { bundleAction(row, it) }

/**
 * The bundles a row may be ticked for (issue #1353): those whose latest revision is unpublished, where the page may
 * publish them or check their impact. A published one has nothing to publish, so it has no box. Pure.
 */
fun selectableBundles(row: ClientOverview?, configs: List<ConfigSummaryView>): List<String> =
    configs.filter { !it.published }.filter { bundleAction(row, it).let { a -> a.publish || a.checkImpact } }.map { it.name }

/**
 * What "Select all unpublished" leaves selected (issue #1353): every selectable bundle, or none when every one already
 * is -- so the one control both selects and clears. Pure.
 */
fun toggleAllUnpublished(selected: Set<String>, selectable: List<String>): Set<String> =
    if (selectable.isNotEmpty() && selected.containsAll(selectable)) emptySet() else selectable.toSet()

/**
 * The bundles a group action acts on (issue #1353): those selected that are still selectable, in the table's order --
 * so a bundle somebody published meanwhile, or one no longer listed, drops out of the selection by itself. Pure.
 */
fun selectedBundles(selected: Set<String>, selectable: List<String>): List<String> = selectable.filter { it in selected }

/**
 * What a bundle holds (issue #1461): its latest revision's slots and entries -- "Empty" for none -- and, when a
 * published revision under it holds something else, that too, by its version (`Empty; v2: 1 slot, 3 entries`). So the
 * table shows at a glance which bundles are no longer used, and which must publish their emptying first. Pure.
 */
fun bundleContentsText(bundle: ConfigSummaryView): String {
    fun text(slots: Int, entries: Int) =
        if (entries == 0) "Empty" else "$slots slot${if (slots == 1) "" else "s"}, $entries entr${if (entries == 1) "y" else "ies"}"
    val latest = text(bundle.slotCount, bundle.entryCount)
    val publishedEntries = bundle.publishedEntryCount
    val publishedVersion = bundle.publishedVersion
    if (bundle.published || publishedEntries == null || publishedVersion == null) return latest
    val published = text(bundle.publishedSlotCount ?: 0, publishedEntries)
    return if (published == latest) latest else "$latest; v$publishedVersion: $published"
}

/**
 * Whether the page of the client [row] deletes its bundles (issue #1461): where it publishes them -- a client without a
 * sandbox, and a sandbox's page for its parent's -- since a bundle is deleted only once its emptying is published. Not
 * a client static here, which takes nothing stored. Pure.
 */
fun deletesBundlesHere(row: ClientOverview?): Boolean = row != null && !row.staticHere && !row.hasSandbox

/**
 * The bundles a row offers **Delete** for (issue #1461): those whose latest revision and latest published revision
 * both hold no entries, as the delete requires, on a page that deletes them ([deletesBundlesHere]). Pure.
 */
fun deletableBundles(row: ClientOverview?, configs: List<ConfigSummaryView>): List<String> = if (!deletesBundlesHere(row)) {
    emptyList()
} else {
    configs.filter { it.entryCount == 0 && (it.publishedEntryCount ?: 0) == 0 }.map { it.name }
}

/** The names of a group as a sentence names them: `main`, or `main and extra`, or `a, b and c`. Pure. */
fun bundleNamesText(names: List<String>): String =
    if (names.size <= 1) names.joinToString() else "${names.dropLast(1).joinToString(", ")} and ${names.last()}"

/**
 * Whether the impact dialog may link its forms (issue #935): the forms are the report's client's -- a sandbox's
 * parent's -- so they open for an administrator across clients, and from any page but a sandbox's, whose session is a
 * user of the sandbox and cannot read them. Pure.
 */
fun impactFormsOpenable(acrossClients: Boolean, onSandboxPage: Boolean): Boolean = acrossClients || !onSandboxPage

/** Where a form of an impact report opens: the raw trait editor (issue #935). Pure. */
fun impactFormHref(gedraId: String): String = routeHref(listOf(HP.page to pageEditForm, HP.gedra to gedraId))
